/**
 * @mc/experiments
 *
 * APIs to render your experiments on the client.
 *
 * Usage
 * ~~~~~
 *
 * This module exports two components–`Experiment` and `Variant`. In order
 * to render an experiment, you'll have to use both of these together. If
 * an experiment is already active, the component(s) will render immediately.
 * If the experiment has not yet been activated, then these components will
 * handle that for you, reaching to the backend to do eligibility checking
 * and activation.
 *
 * Whenever client-side activation occurs, it happens asynchronously. This
 * means that there will be a non-determinant amount of time where we cannot
 * render anything. In some cases, this might be fine. In others, it's less
 * than desired. To handle this case, you can provide a `loading` prop to
 * the `Experiment` component, which will be used to display an intermediary
 * state.
 *
 * Here's an example of how you might handle forking the rendered UI of a
 * control and variant. In this example, we have an experiment named
 * "stevonnie", which has a variant named "fusion".
 *
 * ```js
 * function StevenAndConnie(props) {
 *   return (
 *     <Experiment
 *       name="stevonnie"
 *       loading={<Loading height={200} />}
 *     >
 *       <Variant name="control">
 *         <Steven {...props} />
 *         <Connie {...props} />
 *       </Variant>
 *       <Variant name="fusion">
 *         <Stevonnie {...props} />
 *       </Variant>
 *     </Experiment>
 *   );
 * }
 * ```
 *
 * If the experiment has not been activated, then we'll render the `loading`
 * state until activation finishes. Once we're done activating, the matching
 * variant will render into place. If the user is not eligible for the
 * experiment, then the "control" variant will render. If they are elibile
 * and got bucketed into the "fusion" variant, then that component tree would
 * render to the DOM.
 *
 * If ever something goes awry, then we'll fall back to rendering the control
 * variant (which is better than displaying nothing!).
 *
 * In order to make your experiment accessible to the activation endpoint,
 * you'll need to make sure that it's been added to the `experiments` array
 * of the `\MC\Experiments\ExperimentKey` PHP class. Failing to doing this
 * will result in the activation endpoint 404ing–we do our best to surface
 * this to you in development.
 *
 * Testing
 * ~~~~~~~
 *
 * When testing a component that contains an `Experiment` or `Variant`,
 * you'll want to use our test helper, `mockExperiments`–this allows you to
 * statically declare which variant of a given experiment is active. For
 * instance, if you wanted to test that the UI within a variant named
 * "stevonnie" was working inside of an experiment named "fusion", you would
 * add this to the particular test:
 *
 * ```js
 * import { mockExperiments } from '@mc/experiments';
 *
 * // Somewhere inside your test:
 * mockExperiments({
 *   fusion: 'stevonnie',
 * });
 * ```
 *
 * This would enforce the "stevonnie" variant of the "fusion" experiment to
 * always appear in the DOM, side-stepping loading states.
 *
 * If you're testing multiple experiments, you might want to use the helper
 * to clean up mocks as well, which boils down to importing and calling
 * `clearMockedExperiments`.
 *
 * For examples of these, view `@mc/experiments.test.js`.
 */

import React, { useContext, useEffect, useState } from 'react';

import client from '@mc/networking/http';

// Read the JSON-dump of experiment data provided by React.php. We use
// this value to seed the client-side experiment store created from calling
// `createExperimentStore`.
//
// This payload takes the shape of:
//
// ```json
// [
//   {
//     name: 'experiment-name',
//     variant: 'control',
//   },
// ]
// ```
function getServerActivatedExperiments() {
  if (__TEST__) {
    return [];
  }
  return [];
}

const serverActivatedExperiments = getServerActivatedExperiments();

// A sigil for experiments which have not yet been activated, or whose
// activation is still in-flight.
export const PENDING_VARIANT = '^_^';

// __DEV__-only sigil for bubbling errors upstream
const ERRANT_REQUEST = ':^(';

// Default context sigil for flagging misuse
const MISSING_CONTEXT = 'T_T';

/**
 * Hits the server in order to activate an experiment (if not already active),
 * and return the name of the variant.
 *
 * @param {String} experimentName
 * @return {Promise} A JSON-resolving Promise instance
 */
function activateExperiment(experimentName: $TSFixMe) {
  return client
    .get(`/experiments/activate?name=${experimentName}`)
    .then((response: $TSFixMe) => {
      if (response.ok) {
        return response.json();
      }

      if (__DEV__) {
        if (response.status === 404) {
          return {
            variant: ERRANT_REQUEST,
          };
        }
      }

      throw new Error(
        `Failed to fetch experiment variant for ${experimentName}`,
      );
    });
}

/**
 * If you want to force-view a particular variant of an experiment, we expose
 * a location.search based API. By providing the name of your experiment as
 * well as the particular variant name, you can side-step all async actions
 * and seed the experiment store with that particular variant.
 *
 * For example, when building out or QA-ing the "steven-universe" experiment,
 * you can force the "future" variant by reloading the page with a querystring
 * of: `?experiments=steven-universe:future`.
 *
 * This function does the location parsing to make this API work on the React
 * side.
 *
 * @return {Object} An experiment object with name and variant props.
 */
function readForcedVariantFromSearchParams() {
  const searchParams = new URLSearchParams(window.location.search);

  if (!searchParams.has('experiments')) {
    return;
  }

  // @ts-expect-error TS2531
  const [name, variant] = searchParams.get('experiments').split(':');

  if (variant === undefined) {
    return;
  }

  return {
    name,
    variant,
  };
}

/**
 * View a forced variant of the experiment with Promise return.
 *
 * @param {String} experimentName
 * @return {Promise} A JSON-resolving Promise instance
 */
function getForcedVariantExperiment(experimentName: $TSFixMe) {
  const forcedVariant = readForcedVariantFromSearchParams();

  if (forcedVariant) {
    const { name, variant } = forcedVariant;
    return Promise.resolve({
      name,
      variant,
    });
  }

  return activateExperiment(experimentName);
}

/**
 * A factory for creating our subscription-based, event-emitting experiment
 * store. This store lives outside of React in order to make repurposing
 * experiments a bit simpler (we can remove the necessity of a secondary
 * upstream context-provider by keeping the store outside React). This store
 * is more or less an unbounded cache (we can address this if we ever have
 * a non-trivial number of concurrent experiments), where misses result in
 * asynchronous activation–this means hitting the server and asking, doing
 * whatever eligibility checks, and returning the variant.
 *
 * This store can be seeded with any experiments which have been activated
 * as part of the initial request / initialization of the app, e.g.:
 *
 * ```js
 * [
 *   {
 *     name: 'DNE-814-IA-New-User-33020',
 *     variant: 'control',
 *   },
 *   {
 *     name: 'DNE-814-IA-Existing-User-33020',
 *     variant: 'control',
 *   },
 * ]
 * ```
 *
 * @param {Array} activeExperiments An array of objects with name/variant props
 * @return {Object} The experiment store
 */
function createExperimentStore(activeExperiments = []) {
  const listeners = new Set();
  const experiments = new Map();

  for (let i = 0; i < activeExperiments.length; i++) {
    const { name, variant } = activeExperiments[i];
    experiments.set(name, variant);
  }

  const forcedVariant = readForcedVariantFromSearchParams();

  if (forcedVariant !== undefined) {
    const { name, variant } = forcedVariant;
    experiments.set(name, variant);
  }

  function subscribe(callback: $TSFixMe) {
    listeners.add(callback);

    return () => {
      listeners.delete(callback);
    };
  }

  function read(name: $TSFixMe) {
    if (experiments.has(name)) {
      return experiments.get(name);
    }

    // Bootstrap cache miss with pending value then fetch
    experiments.set(name, PENDING_VARIANT);

    activateExperiment(name)
      .then(({ variant }: $TSFixMe) => {
        experiments.set(name, variant);
        emit(name, variant);
      })
      .catch((error: $TSFixMe) => {
        // In the event that an error occurred, for whatever reason, don't
        // break the UI–just fallback to the control instead.
        experiments.set(name, 'control');
        emit(name, 'control');
        console.error(error);
      });

    // Reads based on pending activations always return pending.
    return PENDING_VARIANT;
  }

  function emit(name: $TSFixMe, variant: $TSFixMe) {
    listeners.forEach((callback) => {
      (callback as $TSFixMe)(name, variant);
    });
  }

  const api = {
    subscribe,
    read,
  };

  if (__TEST__) {
    // Test-only API to clobber the experiment store with arbitrary
    // name => variant mappings.
    (api as $TSFixMe).mock = function mock(mocks: $TSFixMe) {
      Object.entries(mocks).forEach(([name, variant]) => {
        experiments.set(name, variant);
      });
    };

    // Test-only API to flush mocked values.
    (api as $TSFixMe).flush = function flush() {
      experiments.clear();
    };
  }

  return api;
}

// This store lives outside of React entirely. It's plausible that
// we'll want to bring in `useMutableSource` when switching over to
// concurrent mode React, adapting the store as necessary.
const experimentStore = createExperimentStore(serverActivatedExperiments);

const ExperimentContext = React.createContext(MISSING_CONTEXT);

/**
 * The glue for the experiment store and components. Reads from the store and
 * subscribes to updates.
 *
 * @param {String} name The experiment name
 * @return {String} The name of the variant
 */
function useExperiment(name: $TSFixMe) {
  const [variant, setVariant] = useState(() => {
    return experimentStore.read(name);
  });

  // By the time this effect runs, the variant may have already resolved.
  // When this is the case, we can simply update the variant and be done.
  // Otherwise, we add a subscription.
  useEffect(() => {
    // If we're in dev, and the request failed, there's nothing to do.
    if (__DEV__) {
      if (variant === ERRANT_REQUEST) {
        return;
      }
    }

    // If the variant is resolved, there's nothing to do.
    if (variant !== PENDING_VARIANT) {
      return;
    }

    // If the variant has been resolved in the store but not set in state,
    // sync it and be done.
    const maybeResolvedVariant = experimentStore.read(name);
    if (maybeResolvedVariant !== variant) {
      setVariant(maybeResolvedVariant);
      return;
    }

    // If the variant is still pending, add a subscription.
    return experimentStore.subscribe(() => {
      setVariant(experimentStore.read(name));
    });
  }, [name, variant]);

  return variant;
}

type ExperimentProps = {
  __FORCE_TIMEOUT?: boolean;
  children: React.ReactNode;
  loading?: React.ReactNode;
  loadingDelayMS?: number;
  name: string;
};

/**
 * Provides the experiment variant in order for the `Variant` components to
 * work. This component handles any necessary client-side activation, which
 * means that there might be some asynchronous work to handle prior to doing
 * any rendering. As such, it's possible to render a loading state at the
 * level of this component, by providing JSX to the `loading` prop. When
 * building out the loading UI, it's also possible to force this component
 * into its loading state by providing the `__FORCE_TIMEOUT` prop.
 */
function Experiment({
  name,
  children,
  loading = null,
  loadingDelayMS = 300,
  __FORCE_TIMEOUT = false,
}: ExperimentProps) {
  const variant = useExperiment(name);

  // If the variant is already cached, don't worry about the timer.
  const [didTimeout, setDidTimeout] = useState(
    variant === PENDING_VARIANT && loadingDelayMS > 0 ? false : true,
  );

  // In lieu of shoving another loading UI in our users' faces,
  // we can try to side-step this by adding a timer, defaulting
  // to 300 milliseconds, which gates any loading state from
  // rendering. If the user has not provided a loading state, this
  // is all for naught anyway.
  useEffect(() => {
    const timerId = setTimeout(() => {
      setDidTimeout(true);
    }, loadingDelayMS);

    return () => {
      clearTimeout(timerId);
    };
  }, [loadingDelayMS]);

  // If we want to hone in on a particular loading state, we can
  // force it by adding a development-only prop `__FORCE_TIMEOUT`.
  if (__DEV__) {
    if (__FORCE_TIMEOUT) {
      return loading;
    }
  }

  // Return the loading UI if the timeout duration has expired.
  if (variant === PENDING_VARIANT && didTimeout) {
    return loading;
  }

  if (__DEV__) {
    if (variant === ERRANT_REQUEST) {
      return (
        <div
          style={{ padding: '2rem', margin: '2rem', border: '1px solid red' }}
        >
          <span role="img" aria-label="Error">
            ⚠️
          </span>{' '}
          <strong>@mc/experiments Error:</strong> The server was unable to find
          an experiment named <code>{name}</code>. Did you add the experiment
          class to the <code>experiments</code> array of{' '}
          <code>\MC\Experiments\ExperimentKey</code>?
        </div>
      );
    }
  }

  return (
    <ExperimentContext.Provider value={variant}>
      {children}
    </ExperimentContext.Provider>
  );
}

type VariantProps = {
  children: React.ReactNode;
  name: string;
};

/**
 * Render a variant for a given experiment. Must be contained within an
 * Experiment component. Only renders a variant when the name matches
 * the active variant.
 */
function Variant({ name, children }: VariantProps) {
  const variant = useContext(ExperimentContext);

  if (__DEV__) {
    if (variant === MISSING_CONTEXT) {
      throw new Error(
        '<Variant /> must be used inside of an <Experiment /> subtree. ' +
          'Did you forget to render an <Experiment />?',
      );
    }
  }

  if (variant === name) {
    return children;
  }

  return null;
}

function mockExperiments() {
  throw new Error('mockExperiments can only be used in a test environment');
}

function clearMockedExperiments() {
  throw new Error(
    'clearMockedExperiments can only be used in a test environment',
  );
}

// Test-only APIs
if (__TEST__) {
  // Listen. We need this.
  // @ts-expect-error TS2630
  // eslint-disable-next-line no-func-assign,no-shadow
  mockExperiments = function mockExperiments(mocks: $TSFixMe) {
    (experimentStore as $TSFixMe).mock(mocks);
  };

  // Again.
  // @ts-expect-error TS2630
  // eslint-disable-next-line no-func-assign,no-shadow
  clearMockedExperiments = function clearMockedExperiments() {
    (experimentStore as $TSFixMe).flush();
  };
}

export {
  activateExperiment,
  getForcedVariantExperiment,
  Experiment,
  Variant,
  useExperiment,
  mockExperiments,
  clearMockedExperiments,
};
